'use client';
/* eslint-disable @next/next/no-img-element */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FaChevronLeft,
    FaChevronRight,
    FaCompress,
    FaExpand,
    FaPause,
    FaPlay,
    FaTimes,
} from 'react-icons/fa';

import './gallery.css';

const SLIDE_INTERVAL = 4500;

function normalizeMediaItem(item, index, productName) {
    const original = item?.original || item?.media_name || item?.thumbnail || '';
    const thumbnail = item?.thumbnail || item?.original || item?.media_name || original;
    const fallbackAlt = item?.originalAlt || item?.thumbnailAlt || item?.title || `${productName} image ${index + 1}`;

    return {
        ...item,
        original,
        thumbnail,
        originalAlt: fallbackAlt,
        thumbnailAlt: item?.thumbnailAlt || fallbackAlt,
        description: item?.description || item?.title || '',
        isVideo: Boolean(item?.embedUrl),
    };
}

function stripHtml(value = '') {
    return String(value).replace(/<[^>]*>/g, '').trim();
}

export default function Gallery({ media = [], productName = 'Product' }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [openVideo, setOpenVideo] = useState(null);
    const [mediaFilter, setMediaFilter] = useState('all');
    const galleryRef = useRef(null);
    const thumbnailRefs = useRef([]);

    const allItems = useMemo(
        () => media.map((item, index) => normalizeMediaItem(item, index, productName)).filter((item) => item.original),
        [media, productName]
    );

    const imageCount = allItems.filter((item) => !item.isVideo).length;
    const videoCount = allItems.filter((item) => item.isVideo).length;
    const hasImages = imageCount > 0;
    const hasVideos = videoCount > 0;

    const items = useMemo(() => {
        if (mediaFilter === 'images') return allItems.filter((item) => !item.isVideo);
        if (mediaFilter === 'videos') return allItems.filter((item) => item.isVideo);
        return allItems;
    }, [allItems, mediaFilter]);

    const activeItem = items[activeIndex] || items[0];
    const hasMultipleItems = items.length > 1;

    const goToSlide = useCallback(
        (index) => {
            if (!items.length) return;

            setActiveIndex((index + items.length) % items.length);
            setOpenVideo(null);
        },
        [items.length]
    );

    const goToPrevious = useCallback(() => goToSlide(activeIndex - 1), [activeIndex, goToSlide]);
    const goToNext = useCallback(() => goToSlide(activeIndex + 1), [activeIndex, goToSlide]);
    const setFilter = (filter) => {
        setMediaFilter(filter);
        setActiveIndex(0);
        setOpenVideo(null);
    };

    useEffect(() => {
        if (!isPlaying || !hasMultipleItems || openVideo) return undefined;

        const intervalId = window.setInterval(goToNext, SLIDE_INTERVAL);
        return () => window.clearInterval(intervalId);
    }, [goToNext, hasMultipleItems, isPlaying, openVideo]);

    useEffect(() => {
        const selectedThumbnail = thumbnailRefs.current[activeIndex];
        selectedThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [activeIndex]);

    useEffect(() => {
        if (!isFullscreen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsFullscreen(false);
            if (event.key === 'ArrowLeft') goToPrevious();
            if (event.key === 'ArrowRight') goToNext();
        };

        document.body.classList.add('ux-gallery-lock-scroll');
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.classList.remove('ux-gallery-lock-scroll');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [goToNext, goToPrevious, isFullscreen]);

    if (!items.length) return null;

    const galleryClassName = ['ux-product-gallery', isFullscreen ? 'is-fullscreen' : ''].filter(Boolean).join(' ');
    const mediaLabel = `${activeIndex + 1} of ${items.length}`;

    return (
        <section
            ref={galleryRef}
            className={galleryClassName}
            aria-label={`${productName} media gallery`}
            onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') goToPrevious();
                if (event.key === 'ArrowRight') goToNext();
            }}
        >
            <div className="ux-gallery-stage">
                <div className="ux-gallery-counter">{mediaLabel}</div>

                {hasMultipleItems && (
                    <>
                        <button type="button" className="ux-gallery-nav prev" onClick={goToPrevious} aria-label="Previous image">
                            <FaChevronLeft aria-hidden="true" />
                        </button>
                        <button type="button" className="ux-gallery-nav next" onClick={goToNext} aria-label="Next image">
                            <FaChevronRight aria-hidden="true" />
                        </button>
                    </>
                )}

                <div className="ux-gallery-media">
                    {activeItem.isVideo && openVideo === activeItem.embedUrl ? (
                        <div className="ux-gallery-video">
                            <button
                                type="button"
                                className="ux-gallery-close-video"
                                onClick={() => setOpenVideo(null)}
                                aria-label="Close video"
                            >
                                <FaTimes aria-hidden="true" />
                            </button>
                            <iframe
                                title={`${productName} video`}
                                src={activeItem.embedUrl}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={`ux-gallery-image-button ${activeItem.isVideo ? 'is-video' : ''}`}
                            onClick={() => {
                                if (activeItem.isVideo) {
                                    setOpenVideo(activeItem.embedUrl);
                                } else {
                                    setIsFullscreen(true);
                                }
                            }}
                            aria-label={activeItem.isVideo ? `Play video for ${productName}` : `View ${activeItem.originalAlt}`}
                        >
                            <img src={activeItem.original} alt={activeItem.originalAlt} />
                            {activeItem.isVideo && (
                                <span className="ux-gallery-play-badge" aria-hidden="true">
                                    <FaPlay />
                                </span>
                            )}
                        </button>
                    )}
                </div>

                {activeItem.description && (
                    <div className="ux-gallery-caption" title={stripHtml(activeItem.description)}>
                        <span dangerouslySetInnerHTML={{ __html: activeItem.description }} />
                    </div>
                )}
            </div>

            <div className="ux-gallery-controls">
                <button
                    type="button"
                    className="ux-gallery-tool"
                    onClick={() => setIsPlaying((current) => !current)}
                    disabled={!hasMultipleItems || Boolean(openVideo)}
                    aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                    title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                >
                    {isPlaying ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
                </button>
                {(hasImages || hasVideos) && (
                    <div className="ux-gallery-filter-group" aria-label="Filter gallery media">
                        <button
                            type="button"
                            className={mediaFilter === 'images' ? 'is-active' : ''}
                            onClick={() => setFilter('images')}
                            disabled={!hasImages}
                            aria-pressed={mediaFilter === 'images'}
                        >
                            Images
                            <span>{imageCount}</span>
                        </button>
                        <button
                            type="button"
                            className={mediaFilter === 'videos' ? 'is-active' : ''}
                            onClick={() => setFilter('videos')}
                            disabled={!hasVideos}
                            aria-pressed={mediaFilter === 'videos'}
                        >
                            Videos
                            <span>{videoCount}</span>
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    className="ux-gallery-tool"
                    onClick={() => setIsFullscreen((current) => !current)}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'}
                    title={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'}
                >
                    {isFullscreen ? <FaCompress aria-hidden="true" /> : <FaExpand aria-hidden="true" />}
                </button>
            </div>

            {hasMultipleItems && (
                <div className="ux-gallery-thumbnails" role="tablist" aria-label={`${productName} thumbnails`}>
                    {items.map((item, index) => (
                        <button
                            key={`${item.thumbnail}-${index}`}
                            ref={(element) => {
                                thumbnailRefs.current[index] = element;
                            }}
                            type="button"
                            className={`ux-gallery-thumbnail ${index === activeIndex ? 'is-active' : ''}`}
                            onClick={() => goToSlide(index)}
                            role="tab"
                            aria-selected={index === activeIndex}
                            aria-label={`Show ${item.thumbnailAlt}`}
                        >
                            <img src={item.thumbnail} alt={item.thumbnailAlt} loading="lazy" />
                            {item.isVideo && (
                                <span className="ux-gallery-thumbnail-video" aria-hidden="true">
                                    <FaPlay />
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {hasMultipleItems && (
                <div className="ux-gallery-dots" aria-hidden="true">
                    {items.map((item, index) => (
                        <button
                            key={`${item.original}-dot-${index}`}
                            type="button"
                            className={index === activeIndex ? 'is-active' : ''}
                            onClick={() => goToSlide(index)}
                            tabIndex={-1}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
